// Módulos de la portada Pulse — composición por rol (espejo de docs/02-roles-portadas.md).
// En fase 1 real, cada módulo lee de /api/pulse/home; hoy leen los datos demo.
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { G, HOTELS, type Alerta, type RoleId } from '@/constants/data';
import {
  api, type AgeingLive, type Brief, type CajaLive, type HotelDiaLive, type HotelRow,
  type PickupLive, type RejillaLive, type SistemasLive, type Vivo,
} from '@/api/client';
import { AGEING_COLORS, budgetColor, C, F, heatColor, HEAT_RAMP } from '@/constants/theme';
import { fmt, fmtDec, fmtDiaMes, fmtE, fmtK, fmtM, pct1 } from '@/lib/format';
import OnaIcon, { type IconName } from './OnaIcon';
import { Cargando } from './MarcaOna';
import EnsenarNoa from './EnsenarNoa';
import PremioCard from '@/components/PremioCard';
import { useRole } from '@/context/RoleContext';
import { suscribir, valorActual } from '@/lib/refresco';
import { Bar, BottomSheet, Card, Chip, KpiTile, Row, SectionTitle, SegBar, Spark, StatusDot, ZoomDetalle } from './ui';

const I = (name: IconName, color = C.muted) => <OnaIcon name={name} size={17} color={color} />;

/* Trae un bloque que SOLO existe en vivo. Tres estados y ninguno miente:
   cargando (no se sabe todavía), motivo (el servidor dice por qué no) y el dato. */
function useLive<T>(cargar: () => Promise<Vivo<T>>, refrescoMs?: number, recarga = 0) {
  const [dato, setDato] = useState<T | null>(null);
  const [motivo, setMotivo] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  // El tirón hacia abajo cuenta como una recarga más.
  const [global, setGlobal] = useState(valorActual());
  useEffect(() => suscribir(setGlobal), []);
  useEffect(() => {
    let vivo = true;
    let reintento: ReturnType<typeof setTimeout> | null = null;
    /* 🔴 UN FALLO NO BORRA LA PANTALLA, Y SE REINTENTA EN SEGUNDOS (26-08).
       Patricio: la tarjeta del grupo enseñó el cargando «y después directamente
       me desapareció; tuve que forzar cerrar la app». La secuencia era esta:
       una petición falla UNA vez (un parpadeo de red, o el API reiniciándose —
       esa mañana se estaba desplegando), esto hacía `setDato(null)`, la tarjeta
       veía «sin datos» y se esfumaba… y el siguiente intento era el refresco de
       los 10 MINUTOS. Forzar el cierre «lo arreglaba» porque remontar relanza
       la petición. Dos cambios:
         · el dato que ya se tenía SE CONSERVA — un refresco fallido no puede
           borrar lo que el usuario está mirando; el dato lleva sus propias
           marcas de hora, así que no miente;
         · al fallar se reintenta a los 12 segundos, no a los 10 minutos. Cada
           fallo programa UN reintento (no se apilan), así que mientras el
           servidor no esté, esto llama una vez cada 12 s — con diez usuarios de
           dirección es inofensivo y se recupera solo en cuanto vuelve. */
    const leer = () => cargar().then((r) => {
      if (!vivo) return;
      if (r.ok) {
        setDato(r.dato); setMotivo(null);
      } else {
        setMotivo(r.motivo);
        if (reintento) clearTimeout(reintento);
        reintento = setTimeout(leer, 12_000);
      }
      setCargando(false);
    }).catch(() => {
      if (!vivo) return;
      setMotivo('No se ha podido contactar con el servidor.');
      setCargando(false);
      if (reintento) clearTimeout(reintento);
      reintento = setTimeout(leer, 12_000);
    });
    leer();
    const t = refrescoMs ? setInterval(leer, refrescoMs) : null;
    return () => { vivo = false; if (t) clearInterval(t); if (reintento) clearTimeout(reintento); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recarga, global]);
  return { dato, cargando, motivo };
}

/* Un bloque en vivo que hoy no se puede pintar. Se dice, y se dice por qué:
   una tarjeta en blanco parece un fallo de la app y un 0 parece un dato. */
/* 🔴 CARGANDO Y «NO HAY DATO» NO SE PINTAN IGUAL. Los dos usaban este mismo
   cartel de texto, así que «Leyendo la cartera…» y «La cartera no responde» se
   veían idénticos: el mismo gris quieto. Con `cargando`, la espera lleva el
   latido —algo se mueve, o sea que algo está pasando— y el hueco de verdad se
   queda como estaba, quieto, que es lo que tiene que parecer. */
function SinDato({ titulo, motivo, dot, cargando }: {
  titulo: string; motivo: string; dot?: string; cargando?: boolean;
}) {
  return (
    <Card>
      <SectionTitle dot={dot}>{titulo}</SectionTitle>
      {cargando
        ? <Cargando texto={motivo} alto={96} />
        : <Text style={m.sinDato}>{motivo}</Text>}
    </Card>
  );
}

/* ---------- comunes ---------- */

function KpiTrio({ brief }: { brief?: Brief | null }) {
  const { puede } = useRole();
  if (!puede('PRODUCCION')) return null;
  // 🔴 Estos tres eran cifras inventadas del prototipo. Ahora salen del brief:
  // ocupación y ADR los da el servidor, y el RevPAR es su producto — que es
  // literalmente su definición, no una estimación.
  if (brief && (brief.occ != null || brief.adr != null)) {
    const occ = brief.occ, adr = brief.adr ?? null;
    const revpar = occ != null && adr != null ? (adr * occ) / 100 : null;
    const filasOcc: Array<[string, string]> = (brief.occPorPms ?? []).map((o) => [`${o.pms} · ${o.hoteles} hot.`, `${fmtDec(o.media)}%`]);
    const filasAdr: Array<[string, string]> = (brief.adrPorPms ?? []).map((a) => [`${a.pms} · ${fmt(a.rn)} noches`, `${fmtDec(a.adr)} €`]);
    return (
      <View style={m.kpis}>
        <KpiTile value={occ != null ? pct1(occ) : '—'} label="Ocupación hoy" delta={`${brief.hoteles} hoteles`} deltaKind="flat"
          detail={filasOcc.length ? { rows: filasOcc, foot: 'Rejilla de los 4 PMS, cada uno con su doctrina.' } : undefined} />
        <KpiTile value={adr != null ? `${fmtDec(adr)} €` : '—'} label="ADR del mes" delta="neto sin IVA" deltaKind="flat"
          detail={filasAdr.length ? { rows: filasAdr, foot: 'Alojamiento ÷ noches, del folio de cada PMS.' } : undefined} />
        <KpiTile value={revpar != null ? `${fmtDec(revpar)} €` : '—'} label="RevPAR" delta="ADR × ocupación" deltaKind="flat" />
      </View>
    );
  }
  const foot = 'Módulo demo — el detalle real por PMS llega con la siguiente iteración del BFF.';
  return (
    <View style={m.kpis}>
      <KpiTile
        value={pct1(G.occ)} label="Ocupación hoy" delta="▲ +2,1 pts"
        detail={{
          rows: [['Noray · 12 hot.', '89,2%'], ['Onasystem · 2 hot.', '82,5%'], ['Navision · 2 hot.', '80,5%'], ['Avalon · 1 hot.', '92,8%']],
          foot,
        }}
      />
      <KpiTile
        value={`${fmtDec(G.adr)} €`} label="ADR" delta="▲ +4,2%"
        detail={{
          rows: [['Noray', '118,6 €'], ['Onasystem', '96,2 €'], ['Navision', '86,4 €'], ['Avalon', '112,4 €']],
          foot,
        }}
      />
      <KpiTile
        value={`${fmtDec(G.revpar)} €`} label="RevPAR" delta="▲ +6,1%"
        detail={{
          rows: [['Fórmula', 'ADR × ocupación'], ['Noray', '105,8 €'], ['Avalon', '104,3 €'], ['Resto', '—']],
          foot,
        }}
      />
    </View>
  );
}

/* El GOP proyectado del prototipo (2,94 M€ «vs budget») se ha ELIMINADO: era
   una cifra inventada en la portada de Dirección, al lado de datos reales, y
   alguien acabaría creyéndosela. Cuando exista una fuente de verdad para el
   GOP del mes en curso, vuelve — pero con datos, no con un dibujo.
   El GOP real del último mes PUBLICADO sí está: es la tarjeta de Premios Noa. */

/* 🔴 CABEZA Y COLA: SOLO HOTELES DE CONTROL DE GESTIÓN, y al tocar, los que
   quedan fuera. Acuerdo de la demo (docs/15-demo-dg.md).

   Por qué importa quién entra: comparar «cabeza y cola» mezclando hoteles que
   hacen el cierre hotelero con hoteles que no lo hacen es comparar peras con
   manzanas — y la cola se llenaba de los que no tienen presupuesto, que es
   precisamente donde el CEO va a mirar. Los de CG son 39, y son EXACTAMENTE los
   39 que tienen presupuesto en `pl_budget_real`: el mismo conjunto por dos
   caminos distintos, que es la única validación que vale.

   Y los de fuera NO se esconden. Son 14 que facturan 4,2 M€ del mes —el 16% del
   grupo, y la mitad es Ona Don Juan él solo, al 99,5% de ocupación—. Patricio:
   «esos hoteles tendrán que ir dentro de Otros Hoteles». Esconder 4,2 M€ deja un
   agujero que se ve; agruparlos y decir por qué, no. */
function TopBottom({ live }: { live?: HotelRow[] }) {
  // Sin permiso de producción esta tarjeta no tiene nada honesto que enseñar.
  const { puede } = useRole();
  const [abierta, setAbierta] = useState(false);
  if (!puede('PRODUCCION')) return null;
  // Sin Tortugonas: el premio irá sobre el último mes PUBLICADO de CG, no el vivo.
  // Con datos LIVE la tarjeta usa SOLO los hoteles reales (activos). Mientras el
  // budget 2026 no esté cargado, ordena por producción del mes y lo declara.
  const rows: Array<{ n: string; bud: number | null; mes: number; cg: boolean }> = live?.length
    ? live.map((h) => ({ n: h.n, bud: h.bud ?? null, mes: h.mes, cg: h.cg !== false }))
    : HOTELS.map((h) => ({ n: h.n, bud: h.bud, mes: h.mes, cg: true }));
  /* `cg !== false` y no `cg === true`: en demo el campo no viene, y un hotel sin
     marca se trata como que SÍ entra. Si un día el API deja de mandarlo, la
     tarjeta vuelve a lo de antes en vez de quedarse vacía. */
  const enCg = rows.filter((r) => r.cg);
  const fuera = rows.filter((r) => !r.cg).sort((a, b) => b.mes - a.mes);
  const hayBudget = enCg.some((r) => r.bud != null);
  const sorted = [...enCg].sort((a, b) => (hayBudget ? (b.bud ?? -1) - (a.bud ?? -1) : b.mes - a.mes));
  const mesNombre = new Date().toLocaleDateString('es-ES', { month: 'long' });
  const item = (h: (typeof rows)[0], gold: boolean, last: boolean) => (
    <View key={h.n} style={[m.tbRow, last && { borderBottomWidth: 0 }]}>
      <Text style={[m.tbMedal, gold && { color: C.good }]}>{gold ? '△' : '▽'}</Text>
      <Text style={m.tbName} numberOfLines={1}>{h.n.replace('Ona ', '')}</Text>
      {hayBudget && h.bud != null ? (
        <Chip kind={h.bud >= 100 ? 'good' : 'bad'}>{`${h.bud >= 100 ? '+' : ''}${fmtDec(h.bud - 100)}%`}</Chip>
      ) : (
        <Chip kind={gold ? 'good' : 'mut'}>{`${fmt(Math.round(h.mes / 1e3))} k€`}</Chip>
      )}
    </View>
  );

  /* LA CARA DE ATRÁS. No es «el resto de la lista»: es una respuesta a «¿y estos
     por qué no están?», que es lo que va a preguntar quien conozca los hoteles. */
  if (abierta) {
    const total = fuera.reduce((t, h) => t + h.mes, 0);
    return (
      <Pressable onPress={() => setAbierta(false)}>
        <Card>
          <SectionTitle right="Otros Hoteles">Fuera de Control de Gestión</SectionTitle>
          <Text style={m.footNote}>
            Estos hoteles facturan, pero no hacen el cierre hotelero mensual, así que
            no tienen presupuesto contra el que compararse. Por eso no entran en la
            cabeza y cola.
          </Text>
          <View style={{ marginTop: 10 }}>
            {fuera.map((h, i) => (
              <View key={h.n} style={[m.tbRow, i === fuera.length - 1 && { borderBottomWidth: 0 }]}>
                <Text style={m.tbName} numberOfLines={1}>{h.n.replace('Ona ', '')}</Text>
                <Chip kind="mut">{`${fmt(Math.round(h.mes / 1e3))} k€`}</Chip>
              </View>
            ))}
          </View>
          <View style={{ backgroundColor: C.card2, borderRadius: 10, padding: 10, marginTop: 10 }}>
            <Text style={{ fontFamily: F.uiMed, fontSize: 12.5, color: C.ink }}>
              {`${fuera.length} hotel${fuera.length === 1 ? '' : 'es'} · ${fmtE(total)} de producción del mes`}
            </Text>
          </View>
          <Text style={m.footNote}>
            Las centrales y las fichas «Hotel PT» y «Hotel RE» no cuentan como hotel:
            no salen en ninguna de las dos caras.
          </Text>
          <Text style={m.tbVolver}>toca para volver ▴</Text>
        </Card>
      </Pressable>
    );
  }

  return (
    <Pressable onPress={() => (fuera.length ? setAbierta(true) : undefined)}>
      <Card>
        <SectionTitle right={`${enCg.length} de CG`}>
          {hayBudget ? `Contra budget · ${mesNombre}` : `Producción · ${mesNombre} · top y cola`}
        </SectionTitle>
        {sorted.slice(0, 3).map((h) => item(h, true, false))}
        <View style={{ height: 8 }} />
        {sorted.slice(-3).reverse().map((h, i) => item(h, false, i === 2))}
        {!hayBudget && live?.length ? (
          <Text style={m.footNote}>El «contra budget» se enciende solo cuando se cargue el budget 2026 (plantillas Revenue).</Text>
        ) : null}
        {fuera.length ? (
          <Text style={m.tbVolver}>
            {`Solo hoteles de Control de Gestión · toca para ver los ${fuera.length} que quedan fuera ▾`}
          </Text>
        ) : null}
      </Card>
    </Pressable>
  );
}

function AlertPeek({ filter }: { filter?: string }) {
  // Las mismas alertas que cuenta la campanita: overbooking de los 4 PMS y
  // sincronizaciones caídas. Nada escrito a mano.
  /* 🔴 `api.alertas()` cae a los avisos de ejemplo cuando el servidor no
     contesta. Sin mirar `source` la tarjeta enseñaría los de mentira SIN la
     franja ámbar — es decir, peor que antes de conectarla. Se arrastra la
     procedencia y la franja se enciende sola. */
  const { dato, cargando } = useLive<{ v: Alerta[]; demo: boolean }>(
    () => api.alertas().then((r) => ({ ok: true as const, dato: { v: r.data, demo: r.source !== 'live' } })),
    5 * 60e3);
  const todas = dato?.v ?? [];
  const esDemo = dato?.demo ?? false;
  const list = todas.filter((a) => !filter || (a.dep || '').includes(filter)).slice(0, 3);

  if (cargando) return <SinDato cargando titulo="Requiere atención" dot={C.warn} motivo="Comprobando…" />;
  if (!list.length) {
    return (
      <Card demo={esDemo}>
        <SectionTitle dot={C.good}>Requiere atención</SectionTitle>
        <Text style={m.sinDato}>
          {todas.length
            ? `Nada pendiente en tu área. Hay ${todas.length} aviso${todas.length === 1 ? '' : 's'} en otras.`
            : 'Todo en orden: ni overbooking ni sincronizaciones caídas.'}
        </Text>
      </Card>
    );
  }

  const resto = todas.filter((a) => !filter || (a.dep || '').includes(filter)).length - list.length;
  return (
    <Card demo={esDemo}>
      <SectionTitle dot={C.warn} right={resto > 0 ? `+${resto} más →` : undefined}>Requiere atención</SectionTitle>
      {list.map((a, i) => (
        <View key={a.t + i} style={[m.tbRow, i === list.length - 1 && { borderBottomWidth: 0 }]}>
          <View style={{ width: 26, alignItems: 'center' }}>
            {I((a.ic || 'warn') as IconName, a.sev === 'crit' ? C.bad : C.aquaSoft)}
          </View>
          <View style={{ flex: 1 }}>
            <Text style={m.tbName} numberOfLines={1}>{a.t}</Text>
            <Text style={m.subTxt} numberOfLines={2}>{a.m}</Text>
          </View>
        </View>
      ))}
    </Card>
  );
}

/* ---------- Dirección de hotel / regional ---------- */

/* MI HOTEL — el día por punto de venta, tal como sale en Facturación del día.
   Es la pantalla que un director mira por la tarde para saber cómo va el día. */
function MiHotel({ hoteles }: { hoteles?: HotelRow[] }) {
  const mio = (hoteles ?? [])[0];
  const { dato, cargando, motivo } = useLive<HotelDiaLive>(
    () => (mio?.id != null
      ? api.hotelDia(mio.id)
      : Promise.resolve({ ok: false as const, motivo: 'Todavía no sé qué hotel es el tuyo.' })),
    10 * 60e3);

  if (!mio) return <SinDato titulo="Tu hotel" motivo="Tu cuenta no tiene ningún hotel asignado en Hotelos. Pídeselo a Sistemas." />;
  if (cargando) return <SinDato cargando titulo={mio.n.replace(/^Ona /i, '')} motivo="Leyendo los folios del día…" />;
  if (!dato) return <SinDato titulo={mio.n.replace(/^Ona /i, '')} motivo={motivo ?? 'No hay folios de este hotel.'} />;
  if (!dato.hayDatos) {
    return (
      <SinDato
        titulo={`${dato.hotel} · hoy`}
        motivo={`Todavía no hay folios del día. El último con datos es el ${dato.ultimoConDatos ?? '—'}.`}
      />
    );
  }

  const bloque = (t: string, arr: typeof dato.recepcion, sub: { sinIva: number; lineas: number }) =>
    arr.length ? (
      <View style={{ marginTop: 12 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Text style={m.tramoT}>{t}</Text>
          <Text style={m.tramoV}>{fmtE(sub.sinIva)}</Text>
        </View>
        {arr.slice(0, 8).map((p) => (
          <View key={p.punto} style={m.cliRow}>
            <Text style={m.cliN} numberOfLines={1}>{p.punto}</Text>
            <Text style={m.cliV}>{fmtE(p.sinIva)}</Text>
          </View>
        ))}
      </View>
    ) : null;

  return (
    <Card>
      <SectionTitle right={dato.ultSync ? `hasta las ${dato.ultSync}` : undefined}>
        {`${dato.hotel.replace(/^Ona /i, '')} · hoy`}
      </SectionTitle>
      <Text style={m.heroNum}>{fmtE(dato.totalSin)}</Text>
      <Text style={m.subTxt}>neto sin IVA · {fmt(dato.lineas)} líneas</Text>
      {bloque('Recepción', dato.recepcion, dato.subRecepcion)}
      {bloque('POS / F&B', dato.pos, dato.subPos)}
      <Text style={m.footNote}>{dato.nota}</Text>
    </Card>
  );
}

/* MIS HOTELES — para el regional: los suyos, comparables entre sí. No es el
   ranking del grupo recortado: es su zona, con su total y su peso. */
function MisHoteles({ hoteles }: { hoteles?: HotelRow[] }) {
  const hs = [...(hoteles ?? [])].sort((a, b) => b.mes - a.mes);
  if (!hs.length) {
    return <SinDato titulo="Tus hoteles" motivo="Tu cuenta no tiene hoteles asignados en Hotelos. Pídeselo a Sistemas." />;
  }
  const total = hs.reduce((a, h) => a + h.mes, 0);
  return (
    <Card>
      <SectionTitle right={`${hs.length} hoteles`}>{`Tu zona · ${fmtM(total)} en el mes`}</SectionTitle>
      {hs.slice(0, 10).map((h, i) => (
        <View key={h.id ?? h.n} style={[m.tbRow, i === Math.min(hs.length, 10) - 1 && { borderBottomWidth: 0 }]}>
          <Text style={m.tbMedal}>{i + 1}</Text>
          <View style={{ flex: 1 }}>
            <Text style={m.tbName} numberOfLines={1}>{h.n.replace(/^Ona /i, '')}</Text>
            <Text style={m.subTxt}>
              {h.occ != null ? `ocupación ${pct1(h.occ)}` : 'sin ocupación'}
              {h.adr != null ? ` · ADR ${fmtDec(h.adr)} €` : ''}
              {h.d == null ? ' · ayer sin volcar' : ''}
            </Text>
          </View>
          <Text style={m.wfVal}>{fmtK(h.mes)}</Text>
        </View>
      ))}
      <Text style={m.footNote}>
        Solo tus hoteles: el alcance sale de Hotelos. Para añadir o quitar zonas, se cambia allí.
      </Text>
    </Card>
  );
}

/* ---------- Operaciones ---------- */

function OpsToday() {
  return (
    <View style={m.kpis}>
      <KpiTile value={fmt(G.lleg)} label="Llegadas hoy" delta="pico 12–14 h" deltaKind="flat" />
      <KpiTile value={fmt(G.sal)} label="Salidas" delta="86% antes de 11 h" deltaKind="flat" />
      <KpiTile value={fmt(G.inHouse)} label="Huéspedes in-house" delta={`▲ ${pct1(G.occ)} ocup.`} />
    </View>
  );
}

function Rooms() {
  const r = G.rooms;
  const segs = [
    { l: 'Limpias', v: r.limpias, color: C.aqua },
    { l: 'Sucias', v: r.sucias, color: C.gold },
    { l: 'Retenidas', v: r.ret, color: C.muted },
    { l: 'No usar', v: r.nousar, color: C.badDeep },
  ];
  return (
    <Card demo>
      <SectionTitle>Estado de habitaciones · ahora</SectionTitle>
      <SegBar segs={segs} />
      <View style={m.legGrid}>
        {segs.map((x) => (
          <View key={x.l} style={m.legItem}>
            <View style={[m.legDot, { backgroundColor: x.color }]} />
            <Text style={m.legTxt}>{x.l} <Text style={m.legVal}>{fmt(x.v)}</Text></Text>
          </View>
        ))}
      </View>
      <Text style={m.footNote}>Fuente: estado de pisos del PMS de cada hotel, unificado</Text>
    </Card>
  );
}

function Incid() {
  return (
    <Card demo>
      <SectionTitle dot={C.warn}>Incidencias y objetos</SectionTitle>
      <Row icon={I('wrench')} label="Incidencias abiertas" value="17" chip="3 críticas" chipKind="bad" />
      <Row icon={I('case')} label="Objetos perdidos (nuevos)" value="5" chip="2 reclamados" />
      <Row icon={I('cal')} label="Eventos hoy en complejos" value="4" chip="2 bodas" last />
    </Card>
  );
}

/* ---------- Revenue ---------- */

function Pickup() {
  const { dato, cargando, motivo } = useLive<PickupLive>(() => api.pickup());
  if (cargando) return <SinDato cargando titulo="Pickup · últimos 7 días" motivo="Contando altas…" />;
  if (!dato || !dato.disponible) {
    return (
      <SinDato
        titulo="Pickup · últimos 7 días"
        motivo={motivo ?? dato?.nota
          ?? 'Ningún espejo de reservas trae hoy la fecha de alta, que es lo que hace falta para contar el pickup.'}
      />
    );
  }

  const dia = (iso: string) => {
    const d = new Date(iso + 'T00:00:00');
    return ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'][d.getDay()];
  };

  return (
    <Card>
      <SectionTitle right={`${dato.porPms.filter((p) => p.cubierto).length} PMS`}>
        Pickup · últimos 7 días
      </SectionTitle>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
        <View>
          <Text style={m.heroNum}>+{fmt(dato.rn ?? 0)}</Text>
          <Text style={m.subTxt}>room-nights nuevas</Text>
        </View>
        <Spark vals={dato.serie} width={120} height={44} />
      </View>
      <View style={m.chipRow}>
        {dato.mejorDia ? (
          <Chip kind="good">{`▲ mejor día: ${dia(dato.mejorDia.fecha)} +${fmt(dato.mejorDia.rn)}`}</Chip>
        ) : null}
        {dato.reservas != null ? <Chip>{`${fmt(dato.reservas)} reservas`}</Chip> : null}
        {/* Lo que NO entra se dice en la tarjeta, no en una nota al pie que nadie lee. */}
        {dato.faltan.length ? <Chip kind="warn">{`sin ${dato.faltan.join(' · ')}`}</Chip> : null}
      </View>
      <Text style={m.footNote}>{dato.nota}</Text>
    </Card>
  );
}

function Heat() {
  const { dato, cargando, motivo } = useLive<RejillaLive>(() => api.rejilla(30), 10 * 60e3);
  const [todos, setTodos] = useState(false);
  if (cargando) return <SinDato cargando titulo="Ocupación · próximos 30 días" motivo="Leyendo los 4 PMS…" />;
  if (!dato || !dato.filas.length) {
    return <SinDato titulo="Ocupación · próximos 30 días" motivo={motivo ?? 'Los espejos de ocupación no devuelven ningún hotel.'} />;
  }

  const hs = dato.filas.slice(0, 8);
  const ob = dato.filas.reduce((n, f) => n + (f.ob > 0 ? 1 : 0), 0);
  const pms = new Set(dato.filas.map((f) => f.pms)).size;

  const celda = (v: number | null, ancho = 15) => ({
    width: ancho, height: 17, borderRadius: 4, marginRight: 3,
    backgroundColor: v == null ? 'rgba(140,200,215,0.07)'
      : v > dato.umbralOverbooking ? C.bad : heatColor(v),
  });

  return (
    <>
    <Pressable onPress={() => setTodos(true)}>
    <Card>
      <SectionTitle right={`ver los ${dato.filas.length} →`}>Ocupación · próximos 30 días</SectionTitle>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View>
          {hs.map((h) => (
            <View key={h.id} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 3 }}>
              <Text style={m.heatName} numberOfLines={1}>{h.n.replace('Ona ', '')}</Text>
              {/* sin dato ≠ vacío: se pinta apagado, nunca como un 60 % */}
              {h.celdas.map((v, d) => <View key={d} style={celda(v)} />)}
            </View>
          ))}
        </View>
      </ScrollView>
      <View style={m.heatLeg}>
        <Text style={m.legTxt}>60%</Text>
        {HEAT_RAMP.map((c) => <View key={c} style={[m.heatLegCell, { backgroundColor: c }]} />)}
        <Text style={m.legTxt}>100%</Text>
        <View style={[m.heatLegCell, { backgroundColor: C.bad, marginLeft: 6 }]} />
        <Text style={m.legTxt}>overbooking</Text>
      </View>
      {ob ? (
        <Text style={m.footNote}>
          {ob} hotel{ob === 1 ? '' : 'es'} por encima del {String(dato.umbralOverbooking).replace('.', ',')}% algún día — están en Alertas.
        </Text>
      ) : (
        <Text style={m.footNote}>Ningún hotel en overbooking en los próximos 30 días.</Text>
      )}
      <Text style={m.footNote}>
        {`Se enseñan los ${hs.length} primeros de ${dato.filas.length} · toca para ver todos.`}
      </Text>
    </Card>
    </Pressable>

    {/* Todos los hoteles, con su nombre y su media, porque en una lista de 53
        la rejilla sola no dice quién es quién. */}
    <BottomSheet visible={todos} onClose={() => setTodos(false)} maxAlto="92%">
      <Text style={m.hojaT}>Ocupación · próximos 30 días</Text>
      <Text style={m.subTxt}>
        {`${dato.filas.length} hoteles · ${pms} PMS · desde ${dato.fechas[0]?.slice(8, 10)}/${dato.fechas[0]?.slice(5, 7)}`}
      </Text>
      <ScrollView style={{ marginTop: 12 }} showsVerticalScrollIndicator={false} bounces={false} overScrollMode="never">
        {dato.filas.map((h) => {
          const v = h.celdas.filter((c) => c != null) as number[];
          const media = v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
          return (
            <View key={h.id} style={m.hojaFila}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text style={m.hojaN} numberOfLines={1}>{h.n.replace(/^Ona /i, '')}</Text>
                {h.ob ? <Chip kind="bad">{`${h.ob} d >100%`}</Chip> : null}
                <Text style={m.hojaMedia}>{media != null ? `${fmtDec(media)}%` : '—'}</Text>
              </View>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 5 }}>
                <View style={{ flexDirection: 'row' }}>
                  {h.celdas.map((c, d) => <View key={d} style={celda(c, 13)} />)}
                </View>
              </ScrollView>
            </View>
          );
        })}
      </ScrollView>
    </BottomSheet>
    </>
  );
}

function Agencias() {
  const ag: Array<[string, number]> = [['Booking.com', 214300], ['Expedia', 118200], ['TUI', 96400]];
  const mx = ag[0][1];
  return (
    <Card demo>
      <SectionTitle>Comisiones de agencias · agosto</SectionTitle>
      <View style={{ gap: 11 }}>
        {ag.map(([n, v]) => (
          <View key={n}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 }}>
              <Text style={m.tbName}>{n}</Text>
              <Text style={m.wfVal}>{fmtE(v)}</Text>
            </View>
            <Bar pct={(v / mx) * 100} />
          </View>
        ))}
      </View>
      <Text style={m.footNote}>Neto revenue = total − IVA − comisión · mapeo por PMS</Text>
    </Card>
  );
}

/* ---------- Financiero ---------- */

function Cash() {
  const { dato, cargando, motivo } = useLive<CajaLive>(() => api.caja());
  if (cargando) return <SinDato cargando titulo="Caja · ayer" motivo="Sumando los cobros…" />;
  if (!dato) return <SinDato titulo="Caja · ayer" motivo={motivo ?? 'La cartera de clientes no responde.'} />;
  const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
  return (
    <Card>
      <SectionTitle right={ddmm(dato.fecha)}>Caja · ayer</SectionTitle>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
        <View>
          <Text style={m.heroNum}>{fmtE(dato.importe)}</Text>
          <Text style={m.subTxt}>cobros registrados</Text>
        </View>
        <Spark vals={dato.serie} width={100} height={42} color={C.good} />
      </View>
      <View style={m.chipRow}>
        <Chip>{`${fmt(dato.apuntes)} apuntes`}</Chip>
        <Chip kind="good">{`${fmtE(dato.mes)} en el mes`}</Chip>
      </View>
      <Text style={m.footNote}>{dato.nota}</Text>
    </Card>
  );
}

/* AGEING — la tarjeta se DA LA VUELTA (pedido de Patricio): delante el semáforo
   por tramos, detrás a quién hay que llamar. Un ageing del que no sale una
   llamada es un gráfico bonito y poco más. */
/* 🔴 DOS REVERSOS, UNO POR AUDIENCIA. El de siempre —a quién cobrar, tramo a
   tramo— es el que necesita Finanzas: cinco tramos por cinco clientes es su
   trabajo del día. Para Dirección General el acuerdo de la demo pide otra cosa:
   «quién debe pagar a 30 días y cuánto», o sea mirando hacia DELANTE. No se
   apilan las dos en la misma tarjeta con tres toques: en una demo, un tercer
   toque te deja en un sitio que no esperabas. Cada portada lleva el reverso que
   le sirve. */
function Ageing({ vista30 = false }: { vista30?: boolean }) {
  const { dato, cargando, motivo } = useLive<AgeingLive>(() => api.ageing());
  const [detras, setDetras] = useState(false);
  if (cargando) return <SinDato cargando titulo="Ageing clientes" dot={C.warn} motivo="Leyendo la cartera…" />;
  if (!dato) return <SinDato titulo="Ageing clientes" dot={C.warn} motivo={motivo ?? 'La cartera de clientes no responde.'} />;

  const venc = dato.tramos.filter((t) => t.vencido);
  const color = (i: number) => AGEING_COLORS[Math.min(i, AGEING_COLORS.length - 1)];
  /* Si el API es viejo no manda `prox30`: se cae al reverso de tramos en vez de
     enseñar una cara vacía. La app nueva con el servidor sin desplegar es un
     estado real, no una hipótesis. */
  const p30 = vista30 ? dato.prox30 : undefined;
  const cara30 = !!p30;

  return (
    <Pressable onPress={() => setDetras((v) => !v)}>
      <Card>
        <SectionTitle dot={C.warn} right={detras ? '← el resumen' : (cara30 ? 'quién paga a 30 días →' : 'a quién cobrar →')}>
          {`Ageing clientes · ${fmtM(dato.total)}`}
        </SectionTitle>

        {!detras ? (
          <>
            <SegBar segs={venc.map((t, i) => ({ v: Math.max(t.importe, 0), color: color(i) }))} />
            <View style={m.legGrid}>
              {venc.map((t, i) => (
                <View key={t.clave} style={m.legItem}>
                  <View style={[m.legDot, { backgroundColor: color(i) }]} />
                  <Text style={m.legTxt}>{t.texto} <Text style={m.legVal}>{fmtK(t.importe)}</Text></Text>
                </View>
              ))}
            </View>
            <View style={m.chipRow}>
              <Chip kind="bad">{`vencido ${fmtM(dato.vencido)}`}</Chip>
              <Chip>{`no vencido ${fmtM(dato.noVencido)}`}</Chip>
              <Chip>{`${fmt(dato.facturas)} facturas`}</Chip>
            </View>
            <Text style={m.footNote}>
              {cara30
                ? `Toca la tarjeta para ver quién tiene que pagar en los próximos 30 días.`
                : 'Toca la tarjeta para ver a quién hay que cobrar en cada tramo.'}
            </Text>
          </>
        ) : p30 ? (
          <>
            <Text style={m.tramoT}>{`Cobros previstos · próximos 30 días`}</Text>
            <Text style={m.subTxt}>
              {`Aún no vencido. ${fmtM(p30.total)} hasta el ${fmtDiaMes(p30.hasta)}.`}
            </Text>
            <View style={{ marginTop: 10 }}>
              {p30.top.slice(0, 6).map((c, i) => (
                /* La clave lleva el índice: agrupando por `customer_no`, dos
                   clientes distintos pueden resolverse al mismo nombre, y el
                   nombre solo daría claves repetidas. */
                <View key={`${c.n}|${i}`} style={m.cliRow}>
                  <Text style={m.cliN} numberOfLines={1}>{c.n}</Text>
                  <Text style={m.cliV}>{fmtE(c.imp)}</Text>
                  <Text style={m.cliF}>{fmtDiaMes(c.vence)}</Text>
                </View>
              ))}
            </View>
            {/* 🔴 EL RECUENTO DICE CUÁNTOS HAY, NO CUÁNTOS SE VEN. Se enseñan seis
                para que la tarjeta no se estire, y el pie declara el total de
                clientes y de euros: un recorte que no se dice se lee como «esto
                es todo». */}
            <View style={{ backgroundColor: C.card2, borderRadius: 10, padding: 10, marginTop: 10 }}>
              <Text style={{ fontFamily: F.uiMed, fontSize: 12.5, color: C.ink }}>
                {`${fmt(p30.clientes)} cliente${p30.clientes === 1 ? '' : 's'} · ${fmtE(p30.total)} hasta el ${fmtDiaMes(p30.hasta)}`}
              </Text>
            </View>
            {Math.abs(p30.neto - p30.total) >= 1 ? (
              <Text style={m.footNote}>
                {`Hay ${fmtE(p30.total - p30.neto)} en cobros a cuenta dentro del tramo, así que lo que entra de neto son ${fmtE(p30.neto)}.`}
              </Text>
            ) : null}
            <Text style={m.footNote}>{dato.nota}</Text>
          </>
        ) : (
          <>
            {venc.filter((t) => t.top.length).map((t, i) => (
              <View key={t.clave} style={{ marginTop: i ? 12 : 4 }}>
                <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
                  <View style={[m.legDot, { backgroundColor: color(i) }]} />
                  <Text style={m.tramoT}>{t.texto}</Text>
                  <Text style={m.tramoV}>{fmtK(t.importe)}</Text>
                </View>
                {t.top.map((c, j) => (
                  <View key={`${c.n}|${j}`} style={m.cliRow}>
                    <Text style={m.cliN} numberOfLines={1}>{c.n}</Text>
                    <Text style={m.cliV}>{fmtE(c.imp)}</Text>
                  </View>
                ))}
              </View>
            ))}
            <Text style={m.footNote}>{dato.nota}</Text>
          </>
        )}
      </Card>
    </Pressable>
  );
}

function Cierres() {
  return (
    <Card demo>
      <SectionTitle>Cierre contable · julio</SectionTitle>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <Text style={[m.heroNum, { fontSize: 24 }]}>{G.cierres.ok}/{G.cierres.tot}</Text>
        <Text style={m.subTxt}>sociedades cerradas</Text>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
        {Array.from({ length: G.cierres.tot }, (_, i) => (
          <View key={i} style={[m.cierreDot, { backgroundColor: i >= G.cierres.ok ? 'rgba(245,184,75,0.9)' : C.good }]} />
        ))}
      </View>
      <Text style={[m.footNote, { color: C.warn }]}>Pendientes: {G.cierres.pend.join(' · ')}</Text>
    </Card>
  );
}

function Club() {
  return (
    <Card demo>
      <SectionTitle>Club Ona · cuotas</SectionTitle>
      <Row icon={I('card')} label="Cobrado YTD" value={fmtM(G.club.ytd)} chip={`${G.club.plan}% del plan`} chipKind="good" />
      <Row icon={I('mail')} label="Socios en monitorio" value={fmt(G.club.morosos)} chip={`▼ ${Math.abs(G.club.dMor)} este mes`} chipKind="good" last />
      <Text style={m.footNote}>Cada cobro en app = un monitorio menos (fase 3)</Text>
    </Card>
  );
}

/* ---------- Sistemas ---------- */

function PmsHealth() {
  const { dato, cargando, motivo } = useLive<SistemasLive>(() => api.sistemas(), 5 * 60e3);
  // 🔴 «2 fallos» y ahí se acababa la información: había que ir a Hotelos a
  // averiguar cuáles. Ahora la celda se toca y cuenta qué falló y cuándo.
  const [abierto, setAbierto] = useState<string | null>(null);
  if (cargando) return <SinDato cargando titulo="Latido de los 4 PMS" motivo="Preguntando a las sincronizaciones…" />;
  if (!dato) {
    return <SinDato titulo="Latido de los 4 PMS" motivo={motivo ?? 'No hay acceso al registro de sincronizaciones de Hotelos.'} />;
  }
  const color = (e: string) => (e === 'bad' ? C.bad : e === 'warn' ? C.warn : C.good);
  const sel = dato.pms.find((p) => p.pms === abierto) ?? null;

  return (
    <>
      <Card>
        <SectionTitle right="toca para el detalle">Latido de los 4 PMS</SectionTitle>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
          {dato.pms.map((p) => (
            <Pressable key={p.pms} style={m.pmsCell} onPress={() => setAbierto(p.pms)}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <StatusDot color={color(p.estado)} size={9} />
                <Text style={m.pmsName}>{p.pms}</Text>
              </View>
              <Text style={m.pmsMsg}>{p.msg}</Text>
            </Pressable>
          ))}
        </View>
        <Text style={m.footNote}>{dato.nota}</Text>
      </Card>

      <BottomSheet visible={sel != null} onClose={() => setAbierto(null)} maxAlto="88%">
        {sel ? (
          <ScrollView showsVerticalScrollIndicator={false} bounces={false} overScrollMode="never">
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <StatusDot color={color(sel.estado)} size={11} />
              <Text style={m.hojaT}>{sel.pms}</Text>
            </View>
            <Text style={m.subTxt}>
              {sel.hoteles ? `${sel.hoteles} hotel${sel.hoteles === 1 ? '' : 'es'} en la rejilla` : 'sin hoteles en la rejilla'}
              {sel.ultima ? ` · última corrida ${new Date(sel.ultima).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
            </Text>

            {!sel.detalle?.length ? (
              <Text style={[m.sinDato, { marginTop: 14 }]}>
                {sel.silencioso
                  ? 'Este PMS solo deja constancia cuando algo falla, y no hay ninguna en 7 días. Silencio es buena noticia.'
                  : 'No hay corridas registradas de este PMS en los últimos 7 días.'}
              </Text>
            ) : (
              <View style={{ marginTop: 14 }}>
                {sel.detalle.map((f) => (
                  <View key={f.tipo} style={m.hojaFila}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <StatusDot color={f.ok ? C.good : C.bad} size={8} />
                      <Text style={m.hojaN} numberOfLines={2}>{f.que}</Text>
                      <Text style={m.hojaMedia}>{f.cuando ?? '—'}</Text>
                    </View>
                    {/* 🔴 De qué hora hablamos. Antes salía la del último PARTE
                        y los syncs de cada 10 min solo dejan parte cuando
                        fallan: «Ocupación Noray · 20 ago» se leía como «no se
                        sincroniza desde el 20» cuando había corrido hace tres
                        minutos. Ahora se dice si es la hora del espejo o la de
                        una incidencia. */}
                    <Text style={m.subTxt}>
                      {f.esFrescura ? 'último dato en el espejo' : 'último parte registrado'}
                      {f.silencioso ? ' · solo deja parte si falla' : ''}
                      {f.incidencia ? ` · última incidencia ${f.incidencia}` : ''}
                      {f.origen ? ` · ${f.origen}` : ''}
                    </Text>
                    {f.error ? <Text style={m.errTxt}>{f.error}</Text> : null}
                    {f.ok && (f.nuevos || f.actualizados) ? (
                      <Text style={m.subTxt}>
                        {`${fmt(f.nuevos ?? 0)} nuevos · ${fmt(f.actualizados ?? 0)} actualizados`}
                      </Text>
                    ) : null}
                  </View>
                ))}
              </View>
            )}
            <Text style={m.footNote}>
              Se relanza desde Hotelos › Sistemas › Sincronizaciones, con el botón «Sincronizar ahora».
            </Text>
          </ScrollView>
        ) : null}
      </BottomSheet>
    </>
  );
}

function Syncs() {
  const { dato, cargando, motivo } = useLive<SistemasLive>(() => api.sistemas(), 5 * 60e3);
  if (cargando) return <SinDato cargando titulo="Sincronizaciones" motivo="Leyendo las últimas 24 h…" />;
  if (!dato) return <SinDato titulo="Sincronizaciones" motivo={motivo ?? 'El registro de sincronizaciones no responde.'} />;

  const { syncs, cola } = dato;
  return (
    <Card>
      <SectionTitle right={`últimas ${syncs.ventana}`}>Sincronizaciones</SectionTitle>
      <Row icon={<StatusDot color={C.good} />} label="En verde" value={fmt(syncs.verde)} />
      {/* Antes el chip enseñaba los CÓDIGOS (MOV_ELIMINADOS · ONASYS_OCUPACION)
          truncados a mitad. Ahora nombres legibles, y debajo qué pasó. */}
      <Row
        icon={<StatusDot color={syncs.fallos ? C.bad : C.muted} />}
        label="Con fallos"
        value={fmt(syncs.fallos)}
        chip={syncs.fallos ? `${syncs.fallos} de ${syncs.total}` : 'ninguno'}
        chipKind={syncs.fallos ? 'bad' : 'good'}
      />
      {(syncs.fallosDetalle ?? []).map((f) => (
        <View key={f.tipo} style={m.falloFila}>
          <Text style={m.falloQue} numberOfLines={1}>{f.que}</Text>
          <Text style={m.errTxt} numberOfLines={2}>
            {f.error}{f.cuando ? ` · ${f.cuando}` : ''}
          </Text>
        </View>
      ))}
      {cola ? (
        <Row
          icon={I('clock')}
          label="Cola de carga Noray"
          value={fmt(cola.enError)}
          chip={cola.enError ? `${cola.rojos} hotel(es) en rojo` : 'despejada'}
          chipKind={cola.enError ? 'bad' : 'good'}
        />
      ) : null}
      <Row
        icon={I('sync')}
        label="Último chequeo de la cola"
        value={cola?.revisado ?? '—'}
        chip={`${cola?.hoteles ?? 0} hoteles`}
        last
      />
      {dato.sinMapear.length ? (
        <Text style={m.footNote}>
          Tipos de sync que Pulse todavía no sabe a qué PMS pertenecen: {dato.sinMapear.join(', ')}.
        </Text>
      ) : null}
    </Card>
  );
}

/* NOA APRENDE — el cuaderno y su respuesta, en Sistemas.
   🔴 Esto estuvo un rato dentro del chat: en cada «no lo sé» salía un botón
   para enseñarle. Patricio lo cortó, y con razón: improvisar una respuesta en
   caliente no es lo mismo que revisar lo que de verdad se pregunta. Aquí se ve
   la lista entera, con cuántas veces lo han preguntado y cuánta gente — que es
   lo que dice qué merece una respuesta y qué es una pregunta suelta. */
interface NoaGrupo { ejemplo: string; veces: number; sinRespuesta: number; personas: number }

function NoaAprende() {
  // Se recarga sola en cuanto se enseña algo, para que lo resuelto desaparezca
  // de la lista sin tener que salir y volver a entrar.
  const [recarga, setRecarga] = useState(0);
  const [ensenar, setEnsenar] = useState<string | null>(null);
  const [repaso, setRepaso] = useState(false);
  const { dato: d, cargando, motivo } = useLive<{
    total: number; sinRespuesta: number; grupos: NoaGrupo[];
  }>(() => api.noaPreguntas(true), 10 * 60e3, recarga);

  if (cargando) return <SinDato cargando titulo="Noa aprende" motivo="Leyendo el cuaderno…" />;
  if (!d) return <SinDato titulo="Noa aprende" motivo={motivo ?? 'El cuaderno de Noa no responde.'} />;

  const pendientes: NoaGrupo[] = (d.grupos ?? []).filter((g) => g.sinRespuesta > 0).slice(0, 6);

  return (
    <>
      <Card>
        <SectionTitle dot={pendientes.length ? C.warn : C.good} right="últimos 30 días">
          Noa aprende
        </SectionTitle>

        {!pendientes.length ? (
          <Text style={m.sinDato}>
            {d.total
              ? 'Noa ha sabido contestar a todo lo que le han preguntado este mes.'
              : 'Todavía nadie le ha preguntado nada este mes.'}
          </Text>
        ) : (
          <>
            <Text style={m.subTxt}>
              {`${d.sinRespuesta} pregunta${d.sinRespuesta === 1 ? '' : 's'} sin respuesta. `}
              Toca una para decirle qué contestar.
            </Text>
            {pendientes.map((g, i) => (
              <Pressable
                key={g.ejemplo}
                style={[m.tbRow, i === pendientes.length - 1 && { borderBottomWidth: 0 }]}
                onPress={() => setEnsenar(g.ejemplo)}
              >
                <View style={{ flex: 1 }}>
                  <Text style={m.tbName} numberOfLines={2}>{g.ejemplo}</Text>
                  <Text style={m.subTxt}>
                    {g.veces === 1 ? 'una vez' : `${g.veces} veces`}
                    {g.personas > 1 ? ` · ${g.personas} personas` : ''}
                  </Text>
                </View>
                <Text style={m.ensenaCta}>enseñar →</Text>
              </Pressable>
            ))}
          </>
        )}

        <Pressable onPress={() => setRepaso(true)} style={{ paddingTop: 12 }}>
          <Text style={m.verEnsenado}>Ver y editar lo que ya le hemos enseñado →</Text>
        </Pressable>
        <Text style={m.footNote}>
          Las respuestas que se escriban aquí las dirá Noa a quien pregunte algo parecido, y
          siempre DESPUÉS de lo que calcula: nunca taparán una cifra del día.
        </Text>
      </Card>

      <EnsenarNoa
        visible={ensenar != null} pregunta={ensenar ?? ''}
        onClose={() => setEnsenar(null)} onGuardado={() => setRecarga((x) => x + 1)}
      />
      <EnsenarNoa visible={repaso} pregunta="" modo="repaso" onClose={() => setRepaso(false)} />
    </>
  );
}


/* ══════════════════════════════════════════════════════════════════════════
   CUADRO DE MANDOS COMERCIAL — «¿voy a llegar al presupuesto?»

   Criterio de Patricio: los directores son jefes de venta. Así que el orden es
   el de una decisión, no el de un panel: cómo voy HOY → voy a llegar a fin de
   MES → dónde se me va el objetivo → qué pasa en cada outlet.

   🔴 Todo lo que se enseña aquí está cotejado contra Hotelos. El Pool Bar de
   Bahía Blanca da 20.365 / 21.574 / −1.209 / 20.239, los mismos cuatro números
   que la pantalla de Cuenta de explotación Outlets. Si algún día dejan de
   coincidir, manda Hotelos. */
/* `soloOutlets` separa los dos segmentos de Hoteles sin duplicar la tarjeta:
   en «Producción» se ve el presupuesto del hotel y se ocultan los outlets; en
   «Outlets» al revés. Misma máquina, dos vistas. */
export function Comercial({ soloOutlets = false }: { soloOutlets?: boolean }) {
  const { dato, cargando, motivo } = useLive<import('@/api/client').ComercialLive>(
    () => api.comercial(), 10 * 60e3);

  if (cargando) return <SinDato cargando titulo="Tu presupuesto" motivo="Calculando…" />;
  if (!dato || !dato.hoteles.length) {
    return <SinDato titulo="Tu presupuesto" motivo={motivo ?? 'Sin hoteles en tu alcance.'} />;
  }
  const conOutlets = soloOutlets ? dato.hoteles.filter((h) => h.outlets.length) : dato.hoteles;
  if (soloOutlets && !conOutlets.length) {
    return <SinDato titulo="Outlets" motivo="Ninguno de tus hoteles tiene outlets con actividad F&B." />;
  }
  return <>{conOutlets.map((h) => (
    <FichaComercial key={h.id} h={h} varios={conOutlets.length > 1} soloOutlets={soloOutlets} />
  ))}</>;
}

function FichaComercial({ h, varios, soloOutlets = false }: { h: import('@/api/client').ComercialHotel; varios: boolean; soloOutlets?: boolean }) {
  const mes = h.mes;
  const vaBien = mes ? mes.pctReal >= mes.pctMes : true;

  return (
    <Card>
      {/* 🔴 CADA TARJETA DICE DE QUÉ ES. «Las slide de producción y de outlets
          están todas juntas y sin título, por lo que no se entiende nada»
          —Patricio—. El nombre del hotel solo no basta: hace falta saber si lo
          que miras es su presupuesto o sus puntos de venta. */}
      <SectionTitle dot={vaBien ? C.good : C.warn} right={soloOutlets ? `${h.outlets.length} outlets` : (varios ? '' : h.patron)}>
        {`${h.nombre}${soloOutlets ? ' · outlets' : ' · presupuesto'}`}
      </SectionTitle>

      {soloOutlets ? null : (<>
      {/* HOY. Los hoteles que no están en Noray no mandan facturación durante el
          día: se les enseña el último día cerrado y se DICE, en vez de un hueco. */}
      {/* 🔴 «0 €» NO ES LO MISMO QUE «NO HAY DATO». Patricio lo dijo dos veces:
          «se siguen viendo hoteles a 0 euro». El servidor mandaba el hotel con
          total 0 y sin hora, y yo pintaba un 0 € gigante con «−100% sobre el
          día» al lado — que en una portada se lee como catástrofe cuando lo que
          pasa es que ese hotel no manda facturación mientras el día corre.
          Si no hay hora, no hay dato: se dice, y no se compara con nada. */}
      {(() => { const sinDato = !h.hoy.cerrado && !h.hoy.hasta; return (<>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10, marginTop: 2 }}>
        <Text style={m.heroNum}>{sinDato ? 'Sin dato hoy' : (h.hoy.importe != null ? fmtE(h.hoy.importe) : '—')}</Text>
        {h.objetivoDia != null && h.hoy.importe != null && !h.hoy.cerrado && !sinDato ? (
          <Chip kind={h.hoy.importe >= h.objetivoDia ? 'good' : 'bad'}>
            {`${h.hoy.importe >= h.objetivoDia ? '+' : ''}${Math.round((100 * h.hoy.importe) / h.objetivoDia - 100)}% sobre el día`}
          </Chip>
        ) : null}
      </View>
      <Text style={m.subTxt}>
        {h.hoy.cerrado
          ? `Último día cerrado (${h.hoy.fecha}) — este hotel no manda facturación durante el día`
          : sinDato
            ? 'Este hotel no manda facturación mientras el día corre'
            : `Hoy hasta las ${h.hoy.hasta} · provisional`}
        {h.objetivoDia != null && !sinDato ? ` · objetivo ${fmtE(h.objetivoDia)}` : ''}
      </Text>
      </>); })()}

      {/* EL MES. La línea que convierte el presupuesto en algo accionable un
          martes por la tarde: cuánto falta, en cuántos días, a cuánto por día. */}
      {mes ? (
        <>
          <View style={{ height: 7, borderRadius: 99, backgroundColor: C.card2, overflow: 'hidden', marginTop: 14 }}>
            <View style={{ width: `${Math.min(100, mes.pctReal)}%`, height: '100%', backgroundColor: vaBien ? C.good : C.warn }} />
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 5 }}>
            <Text style={m.legTxt}>{`${fmtE(mes.conseguido)} de ${fmtE(mes.presupuesto)}`}</Text>
            <Text style={m.legTxt}>{`${mes.pctReal}% con el ${mes.pctMes}% del mes`}</Text>
          </View>
          {mes.faltan != null && mes.porDia != null ? (
            <View style={{ backgroundColor: C.card2, borderRadius: 10, padding: 10, marginTop: 10 }}>
              <Text style={{ fontFamily: F.uiMed, fontSize: 12.5, color: C.ink }}>
                {`Te faltan ${fmtE(mes.faltan)} en ${mes.diasRestantes} día${mes.diasRestantes === 1 ? '' : 's'}`}
              </Text>
              <Text style={m.subTxt}>
                {`${fmtE(mes.porDia)} al día`}{mes.ayer != null ? ` · ayer hiciste ${fmtE(mes.ayer)}` : ''}
              </Text>
            </View>
          ) : null}
        </>
      ) : (
        <Text style={[m.subTxt, { marginTop: 10 }]}>
          Este hotel no tiene presupuesto cargado, así que no hay contra qué comparar.
        </Text>
      )}

      {/* DÓNDE SE TE VA EL OBJETIVO. Volumen y precio suman exacto la brecha de
          alojamiento, así que no queda un residuo que nadie sabe explicar. */}
      {h.brecha.length ? (
        <View style={{ marginTop: 14 }}>
          <Text style={m.legTxt}>Dónde se te va el objetivo</Text>
          {h.brecha.map((b) => (
            <View key={b.causa} style={m.miniRow}>
              <Text style={m.miniLbl}>{b.que}</Text>
              <Text style={[m.miniVal, { color: b.imp < 0 ? C.bad : C.good }]}>
                {`${b.imp > 0 ? '+' : ''}${fmtE(b.imp)}`}
              </Text>
            </View>
          ))}
        </View>
      ) : null}

      {/* LAS PALANCAS. Con el presupuesto de P&L no hay noches ni precio medio
          presupuestados, así que se dice en vez de enseñar guiones mudos. */}
      {h.palancas && h.palancas.ocBud != null ? (
        <View style={{ marginTop: 12 }}>
          <View style={m.miniRow}>
            <Text style={m.miniLbl}>Ocupación</Text>
            <Text style={m.miniVal}>{`${h.palancas.ocReal ?? '—'}% vs ${h.palancas.ocBud}% ppto`}</Text>
          </View>
          <View style={m.miniRow}>
            <Text style={m.miniLbl}>Precio medio</Text>
            <Text style={m.miniVal}>{`${h.palancas.arrReal ?? '—'} € vs ${h.palancas.arrBud} € ppto`}</Text>
          </View>
        </View>
      ) : null}

      </>)}

      {/* OUTLETS. Todos los del hotel, el que más cuesta arriba. */}
      {h.outlets.length ? (
        <View style={{ marginTop: 14 }}>
          <Text style={m.legTxt}>{`Outlets · ${h.outlets.length}`}</Text>
          {(() => { const veces = new Map<string, number>();
            for (const o of h.outlets) veces.set(o.nombre, (veces.get(o.nombre) || 0) + 1);
            return h.outlets.slice(0, 8); })().map((o) => {
            /* 🔴 «MAR MENOR- TERRAZA AZUL» dentro de una tarjeta que ya se titula
               «Ona Mar Menor» gasta media línea en repetir el hotel, y luego el
               nombre del outlet se corta. Se recorta el prefijo del hotel
               comparando palabra a palabra: así vale para «MAR MENOR-» y para
               «BAHIA BLANCA-» sin una lista de casos. */
            const limpio = (() => {
              const norm = (t: string) => t.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
              const hotelP = norm(h.nombre).split(' ').filter((w) => w !== 'ONA' && w.length > 2);
              let out = o.nombre;
              /* 🔴 PREFIJOS QUE NO SON EL NOMBRE DEL HOTEL. «PI- BAR PISCINA» en
                 Ona Cala Pi, «N/A- PALMIRA PARADISE- LOBBY BAR» en Palmiras: el
                 primero es una abreviatura del hotel y el segundo la marca de
                 dimensión sin asignar. Mi recorte solo quitaba palabras del
                 nombre del hotel, así que ninguno de los dos caía. Se quita
                 cualquier prefijo corto seguido de guion —y el «N/A» siempre—,
                 que es lo que de verdad estorba para leer el outlet. */
              out = out.replace(/^\s*N\/?A\s*[-—·]\s*/i, '');
              out = out.replace(/^\s*[A-Za-zÁÉÍÓÚÑ0-9]{1,4}\s*[-—·]\s+/, '');
              for (const w of hotelP) {
                const re = new RegExp(`^\\s*${w}\\s*[-—·]?\\s*`, 'i');
                if (re.test(norm(out)) || re.test(out)) out = out.replace(re, '');
              }
              return out.replace(/^[-—·\s]+/, '').trim() || o.nombre;
            })();
            /* 🔴 UNA BARRA POR OUTLET — pedido de Patricio: «debajo de cada uno
               pondría una barra para que sepan cuánto le falta para llegar al
               presupuesto». Y con el presupuesto A LA VISTA, no solo la brecha:
               «−0,12 M€» no dice si eso es mucho o poco; «52.300 de 172.000»
               sí. El ancho se acota al 100 % para que pasarse no rompa la fila,
               y cuando pasa se pinta en verde, que es lo que significa. */
            const pctO = o.real != null && o.presupuesto ? (100 * o.real) / o.presupuesto : null;
            return (
            <View key={o.dim} style={{ paddingVertical: 5 }}>
              <View style={m.miniRow}>
                <Text style={m.miniLbl} numberOfLines={1}>{limpio}</Text>
                <Text style={m.miniVal}>
                  {o.real != null ? fmtE(o.real) : '—'}
                  {o.presupuesto ? <Text style={{ color: C.faint }}>{` de ${fmtE(o.presupuesto)}`}</Text> : null}
                </Text>
              </View>
              {pctO != null ? (
                <>
                  <View style={{ height: 5, borderRadius: 99, backgroundColor: C.card2, overflow: 'hidden', marginTop: 4 }}>
                    <View style={{ width: `${Math.min(100, Math.max(0, pctO))}%`, height: '100%', backgroundColor: pctO >= 100 ? C.good : C.warn }} />
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 2 }}>
                    <Text style={m.footNote}>{`${Math.round(pctO)}% del presupuesto`}</Text>
                    <Text style={[m.footNote, { color: (o.brecha ?? 0) < 0 ? C.bad : C.good }]}>
                      {o.brecha != null ? `${o.brecha > 0 ? '+' : ''}${fmtE(o.brecha)}` : ''}
                    </Text>
                  </View>
                </>
              ) : o.presupuesto ? (
                /* 🔴 SE CONTRADECÍA A SÍ MISMO. Ponía «— de 255.219 €» y debajo
                   «sin presupuesto cargado»: hay presupuesto, lo que falta es el
                   REAL. La condición exigía los dos y culpaba siempre al
                   presupuesto. Ahora se distingue, y con presupuesto se pinta la
                   barra a cero, que es la verdad. */
                <>
                  <View style={{ height: 5, borderRadius: 99, backgroundColor: C.card2, marginTop: 4 }} />
                  <Text style={m.footNote}>Sin facturación registrada este mes todavía</Text>
                </>
              ) : (
                <Text style={m.footNote}>Sin presupuesto cargado para este outlet</Text>
              )}
            </View>
            );
          })}
          <Text style={m.footNote}>
            Real del mes contra presupuesto. El mes en curso es provisional hasta que se publique el cierre.
          </Text>
        </View>
      ) : null}
    </Card>
  );
}


/* ══════════════════════════════════════════════════════════════════════════
   TOTALES DE GRUPO — la portada de Dirección General

   🔴 AQUÍ HABÍA UNA FICHA POR HOTEL Y ERA UN ERROR. Patricio, 25-08: «se sigue
   viendo sobrecargada la pantalla principal» y «sigo viendo muchos a 0 euros».
   Las dos cosas eran el mismo problema: 49 fichas, y las de los hoteles que no
   mandan facturación intradía abrían con un 0 € gigante. Una portada con 49
   fichas de presupuesto no es una portada, es un informe.
   Ahora la portada da TOTALES y el detalle vive en Hoteles. */
function TotalesGrupo() {
  const { dato, cargando, motivo } = useLive<import('@/api/client').ComercialLive>(
    () => api.comercial(), 10 * 60e3);
  /* 🔴 MIENTRAS CARGA, EL LATIDO — no la nada. Esto devolvía null durante los
     2-3 segundos del cálculo del comercial, así que la portada se pintaba SIN
     estas tarjetas y de golpe aparecían empujando lo demás hacia abajo
     («parece que no hay nada y de golpe te aparecen» — Patricio, 26-08, con el
     Ageing al lado cargando bien, que era el contraste que lo delataba). Un
     hueco que luego se rellena es un salto de página; una tarjeta que late dice
     «esto existe y está viniendo». El null se queda solo para el caso SIN
     datos (rol sin producción), donde no habrá tarjeta nunca. */
  if (cargando) {
    return <SinDato cargando titulo="Grupo contra presupuesto" motivo="Sumando los 39 hoteles de Control de Gestión…" />;
  }
  /* Fallo con motivo → se dice y se espera el reintento (12 s), no se esfuma:
     una tarjeta que desaparece parece que nunca existió. El null queda solo
     para «cargó bien y este rol no tiene nada aquí», que sí es no-tarjeta. */
  if (!dato?.hoteles?.length) {
    if (motivo) return <SinDato titulo="Grupo contra presupuesto" dot={C.warn} motivo={`${motivo} Reintentando…`} />;
    return null;
  }

  const hs = dato.hoteles;
  const suma = (f: (h: typeof hs[0]) => number | null | undefined) =>
    hs.reduce((n, h) => n + (f(h) ?? 0), 0);

  const mesReal = suma(h => h.mes?.conseguido);
  const mesBud = suma(h => h.mes?.presupuesto);
  const anioReal = suma(h => h.anio?.real);
  const anioBud = suma(h => h.anio?.presupuesto);
  const outReal = suma(h => h.outlets.reduce((n, o) => n + (o.real ?? 0), 0));
  const outBud = suma(h => h.outlets.reduce((n, o) => n + (o.presupuesto ?? 0), 0));
  /* El % del mes transcurrido es el mismo para todos: se toma el de cualquiera
     que lo traiga, no se promedia — promediar porcentajes no significa nada. */
  const conBud = hs.filter(h => h.mes).length;

  return (
    <>
      {/* El número de hoteles va A LA VISTA: son los de Control de Gestión, los
          únicos con presupuesto 2026 cargado, y sin decirlo el total parece del
          grupo entero. */}
      <BarraTotal titulo="Mes del grupo contra presupuesto" real={mesReal} bud={mesBud}
        derecha={`${conBud} hoteles en CG`}
        detalle={hs.filter(h => h.mes).map(h => ({
          n: h.nombre, real: h.mes!.conseguido, bud: h.mes!.presupuesto,
        }))} />
      <BarraTotal titulo="Año del grupo contra presupuesto" real={anioReal} bud={anioBud}
        derecha={`${conBud} hoteles · con el mes en curso`}
        detalle={hs.filter(h => h.anio).map(h => ({
          n: h.nombre, real: h.anio!.real, bud: h.anio!.presupuesto,
        }))} />
      {/* 🔴 LOS OUTLETS SE QUITAN DE LA PORTADA. Decisión de Patricio el 25-08:
          el real del mes en curso sale de la contabilidad, que todavía no tiene
          los asientos, así que la cifra enseñaría presupuestos contra huecos —
          se vio en Valle Romano, La Piazza y el Pool Bar del Ritual. Vuelve
          cuando el mes en curso se lea de Noray. «Es preferible mostrarle algo
          fiable, comprobable en Hotelos.» */}
    </>
  );
}

/* Una barra y dos cifras. Es la tarjeta que Patricio dijo que le parecía genial,
   así que se reusa tal cual para mes, año y outlets en vez de inventar tres.
 *
 * 🔴 Y AL TOCARLA, LOS HOTELES UNO A UNO. Patricio: «¿podemos hacer el efecto de
 * la imagen 2 y mostrar los hoteles con su budget y su real con una barra de
 * estado?». El total contra presupuesto contesta «cómo va el grupo»; la pregunta
 * siguiente es siempre «¿y quién lo está tirando?», y hasta ahora había que
 * salirse de la portada para saberlo.
 *
 * Ordenados por PORCENTAJE y no por euros: la lista sirve para ver quién va
 * flojo, y un hotel pequeño al 60 % importa más que uno grande al 99 %. Los
 * mejores arriba y los rezagados al final, que es donde el ojo acaba.
 */
function BarraTotal({ titulo, real, bud, derecha, detalle }: {
  titulo: string; real: number; bud: number; derecha?: string;
  detalle?: Array<{ n: string; real: number; bud: number }>;
}) {
  const [abierto, setAbierto] = useState(false);
  if (!bud) return null;
  const pct = (100 * real) / bud;
  const falta = Math.max(0, bud - real);
  /* Solo los que tienen presupuesto: dividir por cero pinta una barra infinita. */
  const filas = (detalle ?? []).filter(d => d.bud > 0)
    .map(d => ({ ...d, pct: (100 * d.real) / d.bud }))
    .sort((a, b) => b.pct - a.pct);

  const tarjeta = (
    <Card>
      <SectionTitle dot={pct >= 100 ? C.good : C.warn} right={derecha}>{titulo}</SectionTitle>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10, marginTop: 2 }}>
        <Text style={m.heroNum}>{fmtE(real)}</Text>
        <Chip kind={pct >= 100 ? 'good' : 'bad'}>{`${Math.round(pct)}%`}</Chip>
      </View>
      {/* La misma rampa que el detalle de dentro (Patricio, 26-08: «debemos usar
          el mismo degradé de cuando entramos»). Retiro lo del ámbar-veredicto:
          tener fuera un idioma y dentro otro para EL MISMO dato era peor que
          cualquiera de los dos idiomas. El veredicto lo sigue dando el chip. */}
      <View style={{ height: 8, borderRadius: 99, backgroundColor: C.card2, overflow: 'hidden', marginTop: 10 }}>
        <View style={{ width: `${Math.min(100, pct)}%`, height: '100%', backgroundColor: budgetColor(pct) }} />
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 5 }}>
        <Text style={m.legTxt}>{`de ${fmtE(bud)}`}</Text>
        <Text style={m.legTxt}>{falta > 0 ? `faltan ${fmtE(falta)}` : 'presupuesto cubierto'}</Text>
      </View>
      {filas.length ? (
        <Text style={m.tbVolver}>{`toca para ver los ${filas.length} hoteles, uno a uno ▾`}</Text>
      ) : null}
    </Card>
  );

  if (!filas.length) return tarjeta;

  return (
    <>
      <Pressable onPress={() => setAbierto(true)}>{tarjeta}</Pressable>
      <ZoomDetalle
        open={abierto} onClose={() => setAbierto(false)}
        valor={`${Math.round(pct)}%`} etiqueta={titulo}
        pie={`${fmtE(real)} de ${fmtE(bud)}. Presupuesto de pl_budget_real, el mismo que lee Hotelos. `
           + 'Ordenados por % conseguido: los rezagados, al final.'}
      >
        {filas.map((d) => {
          const va = d.pct >= 100;
          /* 🔴 EL € NO SE PARTE (Patricio, 26-08: «para no poner debajo el símbolo €
             como Ritual, podés jugar acortando un poco más el nombre»). Dos medidas
             juntas: el nombre pierde los prefijos que no distinguen a nadie («Ona»,
             «Hotel», «Be Live» y los paréntesis de cadena) y se acota al 42 % de la
             fila; y el importe va a UNA línea con letra que se encoge sola si aun
             así no cabe (adjustsFontSizeToFit) — mejor una cifra un punto más
             pequeña que un € huérfano en la línea de abajo. */
          const nom = d.n.replace(/^(Ona|Hotel|Be ?live)\s+/i, '').replace(/\s*\(.*\)\s*$/, '');
          return (
            <View key={d.n} style={{ paddingVertical: 6 }}>
              <View style={m.miniRow}>
                <Text style={[m.miniLbl, { maxWidth: '42%' }]} numberOfLines={1}>{nom}</Text>
                <Text style={[m.miniVal, { flex: 1 }]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.72}>
                  {fmtE(d.real)}
                  <Text style={{ color: C.faint }}>{` de ${fmtE(d.bud)}`}</Text>
                </Text>
              </View>
              {/* La rampa de la marca en vez del ámbar: nueve barras del mismo
                  color no se comparan, y con el degradé el ojo ordena la lista
                  solo. El ámbar se queda en la tarjeta grande, que da UN veredicto
                  («llega» / «no llega») y ahí el color significa algo distinto. */}
              <View style={{ height: 5, borderRadius: 99, backgroundColor: C.card2, overflow: 'hidden', marginTop: 4 }}>
                <View style={{ width: `${Math.min(100, Math.max(0, d.pct))}%`, height: '100%', backgroundColor: budgetColor(d.pct) }} />
              </View>
              {/* 🔴 TODOS LOS NÚMEROS EN LA MISMA COLUMNA DE LA DERECHA (Patricio,
                  26-08). El importe ya iba a la derecha y el porcentaje se quedaba
                  a la izquierda, así que había que cruzar la fila con la vista para
                  leer las dos cifras de un mismo hotel. Y «budget» en vez de
                  «presupuesto» porque es la mitad de largo y aquí lo que sobra es
                  ancho — es además la palabra que usa el resto de la app. */}
              <Text style={[m.footNote, { marginTop: 2, textAlign: 'right', color: va ? C.aqua : C.faint }]}>
                {`${Math.round(d.pct)}% del budget`}
              </Text>
            </View>
          );
        })}
      </ZoomDetalle>
    </>
  );
}

/* ---------- RRHH ---------- */

function RrhhCoste() {
  const d = ((G.rrhh.coste - G.rrhh.bud) / G.rrhh.bud) * 100;
  return (
    <Card demo>
      <SectionTitle>Coste de personal · julio</SectionTitle>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10 }}>
        <Text style={m.heroNum}>{fmtM(G.rrhh.coste)}</Text>
        <Chip kind="good">{`${fmtDec(d)}% vs budget`}</Chip>
      </View>
      <View style={{ marginTop: 12, gap: 5 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Text style={m.legTxt}>Real</Text>
          <Text style={m.legTxt}>Budget {fmtM(G.rrhh.bud)}</Text>
        </View>
        <Bar pct={(G.rrhh.coste / G.rrhh.bud) * 100} height={10} />
      </View>
      <View style={m.chipRow}>
        <Chip>{`ratio s/TNR ${pct1(G.rrhh.ratio)}`}</Chip>
        <Chip>Payroll budget por outlet</Chip>
      </View>
    </Card>
  );
}

function Plantilla() {
  return (
    <Card demo>
      <SectionTitle>Plantilla</SectionTitle>
      <Row icon={I('people')} label="Personas activas" value={fmt(G.rrhh.plantilla)} chip={`▲ +${G.rrhh.dPl}`} chipKind="good" />
      <Row icon={I('alarm')} label="Horas extra · julio" value={`${fmt(G.rrhh.hextra)} h`} chip="Palmira 214 h" chipKind="warn" />
      <Row icon={I('sync')} label="Rotación YTD" value="11,4%" chip="▼ −1,2 pts" chipKind="good" last />
    </Card>
  );
}

/* ---------- Asset ---------- */

function AssetKpis() {
  return (
    <View style={{ gap: 10 }}>
      <View style={m.kpis}>
        <KpiTile value={String(G.asset.activos)} label="Activos en cartera" delta="3 países" deltaKind="flat" />
        <KpiTile value={fmt(G.asset.uds)} label="Unidades" delta={`${fmt(G.asset.enlazadas)} enlazadas`} deltaKind="flat" />
      </View>
      <View style={m.kpis}>
        <KpiTile value={String(G.asset.contratos)} label="Contratos condohotel" delta="explotación" deltaKind="flat" />
        <KpiTile value={fmtM(G.asset.renta)} label="Renta + fees anual" delta="▲ +2,8%" />
      </View>
    </View>
  );
}

function Vencimientos() {
  const evs: Array<[string, string, boolean]> = [
    ['Ona Cala Pi · alquiler', 'Vence marzo 2027 · en 7 meses · renta 640 k€/año', true],
    ['Jardines Paraisol · gestión', 'Vence junio 2027 · opción de prórroga +5', false],
    ['3 contratos más', 'Vencen en 2028 · sin alertas', false],
  ];
  return (
    <Card demo>
      <SectionTitle dot={C.warn}>Próximos vencimientos</SectionTitle>
      <View style={m.tl}>
        {evs.map(([t, msg, isWarn]) => (
          <View key={t} style={m.tlEv}>
            <View style={[m.tlDot, { borderColor: isWarn ? C.warn : C.aqua }]} />
            <Text style={m.tbName}>{t}</Text>
            <Text style={m.subTxt}>{msg}</Text>
          </View>
        ))}
      </View>
    </Card>
  );
}

function Unlink() {
  return (
    <Card demo>
      <SectionTitle>Calidad del dato</SectionTitle>
      <Row icon={I('link')} label="Unidades sin enlazar a hotel" value="1.010" chip="revisar" chipKind="warn" />
      <Row icon={I('pin')} label="Hoteles sin coordenadas" value="6" chip="mapa" last />
    </Card>
  );
}

/* ---------- composición por rol ---------- */

export function RoleModules({ role, hoteles, brief }: { role: RoleId; hoteles?: HotelRow[]; brief?: Brief | null }) {
  switch (role) {
    case 'dg':
      /* Sin AlertPeek: «requiere tu atención a dirección general en la pantalla
         principal no, ya tiene la campanilla» — Patricio, 25-08. */
      return (<><KpiTrio brief={brief} /><TotalesGrupo /><Ageing vista30 /><TopBottom live={hoteles} /></>);
    case 'dh':
      return (<><KpiTrio brief={brief} /><Comercial /><MiHotel hoteles={hoteles} /><Heat /><AlertPeek /></>);
    case 'reg':
      return (<><KpiTrio brief={brief} /><TotalesGrupo /><MisHoteles hoteles={hoteles} /><Heat /><AlertPeek /></>);
    case 'ops':
      return (<><OpsToday /><Rooms /><Incid /><AlertPeek filter="ops" /></>);
    case 'rev':
      return (<><KpiTrio brief={brief} /><Pickup /><Heat /><Agencias /></>);
    case 'fin':
      return (<><Cash /><Ageing /><Cierres /><Club /></>);
    case 'sis':
      return (<><PmsHealth /><Syncs /><PremioCard /><NoaAprende /><AlertPeek filter="sis" /></>);
    case 'rrhh':
      return (<><RrhhCoste /><Plantilla /></>);
    case 'ast':
      return (<><AssetKpis /><Vencimientos /><Unlink /></>);
  }
}

const m = StyleSheet.create({
  /* Filas de detalle del cuadro de mandos comercial. */
  miniRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 10, paddingVertical: 4 },
  miniLbl: { fontFamily: F.ui, fontSize: 12, color: C.muted, flexShrink: 1 },
  miniVal: { fontFamily: F.uiSemi, fontSize: 12.5, color: C.ink, fontVariant: ['tabular-nums'], textAlign: 'right' },
  kpis: { flexDirection: 'row', gap: 10 },
  heroNum: { fontFamily: F.display, fontSize: 32, color: '#fff' },
  subTxt: { fontFamily: F.ui, fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 15 },
  footNote: { fontFamily: F.ui, fontSize: 10, color: C.faint, marginTop: 10, lineHeight: 14 },
  tbVolver: { fontFamily: F.uiMed, fontSize: 11, color: C.aquaSoft, marginTop: 10, lineHeight: 15 },
  tramoT: { fontFamily: F.uiSemi, fontSize: 12, color: C.ink, flex: 1 },
  tramoV: { fontFamily: F.uiBold, fontSize: 12, color: C.muted, fontVariant: ['tabular-nums'] },
  cliRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 10, paddingVertical: 4, paddingLeft: 17 },
  /* 🔴 EL NOMBRE EMPUJA, EL IMPORTE NO SE MUEVE (Patricio, 26-08: «los números
     tienen que estar alineados»). Antes el nombre solo encogía (flexShrink) y el
     importe iba suelto en el space-between: cada fila acababa con su cifra en un
     sitio. Con el nombre a flex:1 y el importe en columna fija alineada a la
     derecha, los millares caen unos debajo de otros, que es lo que permite
     compararlos de un vistazo. */
  cliN: { fontFamily: F.ui, fontSize: 11.5, color: C.muted, flex: 1 },
  cliV: { fontFamily: F.uiSemi, fontSize: 11.5, color: C.ink, fontVariant: ['tabular-nums'], width: 104, textAlign: 'right' },
  cliF: { fontFamily: F.ui, fontSize: 11, color: C.faint, width: 46, textAlign: 'right' },
  ensenaCta: { fontFamily: F.uiSemi, fontSize: 11.5, color: C.aquaSoft },
  hojaT: { fontFamily: F.displayMed, fontSize: 20, color: C.ink },
  hojaFila: { paddingVertical: 9, borderBottomColor: C.line, borderBottomWidth: 1 },
  hojaN: { fontFamily: F.uiMed, fontSize: 12.5, color: C.ink, flex: 1 },
  hojaMedia: { fontFamily: F.uiBold, fontSize: 12, color: C.muted, fontVariant: ['tabular-nums'] },
  errTxt: { fontFamily: F.ui, fontSize: 11, color: C.bad, marginTop: 4, lineHeight: 16 },
  falloFila: { paddingVertical: 7, paddingLeft: 26 },
  falloQue: { fontFamily: F.uiSemi, fontSize: 12, color: C.ink },
  verEnsenado: { fontFamily: F.uiSemi, fontSize: 12, color: C.aquaSoft },
  sinDato: { fontFamily: F.ui, fontSize: 12, color: C.muted, lineHeight: 17, marginTop: 2 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  wfRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  wfLabel: { width: 100, fontFamily: F.ui, fontSize: 11.5, color: C.muted },
  wfTrack: { flex: 1, height: 10, borderRadius: 6, backgroundColor: 'rgba(140,200,215,0.08)' },
  wfVal: { width: 64, textAlign: 'right', fontFamily: F.uiBold, fontSize: 11.5, color: C.ink, fontVariant: ['tabular-nums'] },
  tbRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: C.line },
  tbMedal: { width: 26, textAlign: 'center', fontSize: 15, color: C.muted },
  tbName: { flex: 1, fontFamily: F.uiMed, fontSize: 13, color: C.ink },
  legGrid: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 12, rowGap: 7 },
  legItem: { flexDirection: 'row', alignItems: 'center', gap: 7, width: '50%' },
  legDot: { width: 9, height: 9, borderRadius: 3 },
  legTxt: { fontFamily: F.ui, fontSize: 11.5, color: C.muted },
  legVal: { fontFamily: F.uiBold, color: C.ink },
  heatName: { width: 104, fontFamily: F.ui, fontSize: 10, color: C.muted, paddingRight: 8 },
  heatLeg: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 10 },
  heatLegCell: { width: 14, height: 9, borderRadius: 3 },
  cierreDot: { width: 14, height: 14, borderRadius: 5 },
  pmsCell: { width: '47%', flexGrow: 1, backgroundColor: C.bg2, borderColor: C.line, borderWidth: 1, borderRadius: 16, padding: 12 },
  pmsName: { fontFamily: F.uiSemi, fontSize: 13, color: C.ink },
  pmsMsg: { fontFamily: F.ui, fontSize: 10.5, color: C.muted, marginTop: 6, lineHeight: 15 },
  tl: { borderLeftWidth: 2, borderLeftColor: 'rgba(30,211,199,0.25)', marginLeft: 7, paddingLeft: 18, gap: 14 },
  tlEv: {},
  tlDot: { position: 'absolute', left: -24.5, top: 3, width: 11, height: 11, borderRadius: 6, backgroundColor: C.card, borderWidth: 2.5 },
});
